// ----------------------------------------------------------------------
// (c) Copyright 2019, Dark Star Systems, Inc.  All rights reserved.
// This file may contain proprietary and confidential information.
// DO NOT COPY or distribute in any form without prior written consent.
// ----------------------------------------------------------------------

import * as Three from 'three'

/**
 * Creates an arrow for visualizing directions
 *
 * Parameters:
 *  dir - Vector3
 *  origin - Vector3
 *  length - Number
 *  color - color in hex value
 *  headLength - Number
 *  headRadius - Number
 */
export class Arrow3DHelper extends Three.Group {
  cone: Three.Mesh
  line: Three.Mesh
  constructor(dir: Three.Vector3,
              origin: Three.Vector3,
              length: number,
              color: number | Three.Color,
              lineRadius?: number,
              headLength?: number,
              headRadius?: number
  ) {
    super()
    this.name = 'Arrow3D'
    this.position.copy(origin)

    // create canonical geometries
    const lineGeometry = new Three.CylinderBufferGeometry(1.0, // radiusTop
                                                          1.0, // radiusBottom
                                                          1.0, // height
                                                          7, 1) // radial/height segments
    lineGeometry.translate(0, 0.5, 0)
    const coneGeometry = new Three.CylinderBufferGeometry(0.0,
                                                          1.0,
                                                          1.0,
                                                          15, 1)
    coneGeometry.translate(0, 0.5, 0)

    // create material
    const materialProps = {
      color: color,
      metalness: 0.2,
      roughness: 0.5,
    }
    const material = new Three.MeshStandardMaterial(materialProps)

    // create meshes
    this.cone = new Three.Mesh(coneGeometry, material)
    this.cone.matrixAutoUpdate = false
    this.add(this.cone)
    this.line = new Three.Mesh(lineGeometry, material)
    this.line.matrixAutoUpdate = false
    this.add(this.line)

    this.setDirection(dir)

    headLength = (headLength !== undefined) ? headLength : 0.2
    headRadius = (headRadius !== undefined) ? headRadius : 0.2

    this.setSizes(length, lineRadius, headLength, headRadius)
  }

  setDirection(dir: Three.Vector3) {
    const upAxis = new Three.Vector3(0, 1, 0) // original axis of cylinder (points up)
    // rotate to desired axis
    this.quaternion.setFromUnitVectors(upAxis, dir.clone().normalize())
  }

  /**
   * Set sizes of parts of the arrow
   * length is in units; all others are fractions of length
   */
  setSizes(length: number, lineRadius?: number,
           headLength?: number, headRadius?: number) {
    if ( lineRadius === undefined ) lineRadius = 0.1
    if ( headLength === undefined ) headLength = 0.2
    if ( headRadius === undefined ) headRadius = 0.2

    lineRadius *= length
    headLength *= length
    headRadius *= length

    // canonical "line" is length 1, radius 1, pointing +y
    this.line.scale.set(lineRadius,
                        Math.max(0, length - headLength),
                        lineRadius)
    this.line.updateMatrix()

    // canonical cone is radius 1, length 1, pointing +y, wide end at origin
    this.cone.scale.set(headRadius, headLength, headRadius)
    this.cone.position.y = Math.max(0, length - headLength),
    this.cone.updateMatrix()
  }
}

// A visible 3d axis object to use with controllers
export class Axis3DHelper extends Three.Group {
  xAxis: Arrow3DHelper
  yAxis: Arrow3DHelper
  zAxis: Arrow3DHelper
  constructor(origin: Three.Vector3,
              length: number,
              lineRadius?: number,
              headLength?: number,
              headRadius?: number) {
    super()
    this.name = 'Axis3DHelper'
    this.xAxis = new Arrow3DHelper(new Three.Vector3(1, 0, 0),
                                   origin, length, 0xff0000,
                                   lineRadius, headLength, headRadius)
    this.xAxis.name = 'xAxis Arrow3D'
    this.yAxis = new Arrow3DHelper(new Three.Vector3(0, 1, 0),
                                   origin, length, 0x00ff00,
                                   lineRadius, headLength, headRadius)
    this.yAxis.name = 'yAxis Arrow3D'
    this.zAxis = new Arrow3DHelper(new Three.Vector3(0, 0, 1),
                                   origin, length, 0x0000ff,
                                   lineRadius, headLength, headRadius)
    this.zAxis.name = 'zAxis Arrow3D'
    this.add(this.xAxis)
    this.add(this.yAxis)
    this.add(this.zAxis)
    this.userData = {DSS: {objType: 'asset', url: 'axis-3d', name: 'axis-3d'}}
  }

  setSizes(length: number, lineRadius?: number,
           headLength?: number, headRadius?: number) {
    this.xAxis.setSizes(length, lineRadius, headLength, headRadius)
    this.yAxis.setSizes(length, lineRadius, headLength, headRadius)
    this.zAxis.setSizes(length, lineRadius, headLength, headRadius)
  }
}
